import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TooltipProvider } from './context/TooltipContext';
import ProtectedRoute from './components/ProtectedRoute';
import ArchiveBanner from './components/ArchiveBanner';

// Auth
import LoginPage    from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPassword from './pages/auth/ResetPassword';

// Contestant
import ContestantDashboard   from './pages/contestant/ContestantDashboard';
import ContestantSubmit      from './pages/contestant/ContestantSubmit';
import ContestantProfile     from './pages/contestant/ContestantProfile';
import ContestantPayment     from './pages/contestant/ContestantPayment';
import ContestantSongReview  from './pages/contestant/ContestantSongReview';

// Heat selection
import HeatSelection from './pages/HeatSelection';

// Shown for any address matching no route (replaces a silent redirect to /login)
import NotFoundPage from './pages/NotFoundPage';

// Heat confirmation (public — no auth)
import HeatConfirm from './pages/HeatConfirm';
import JudgeHeatConfirm from './pages/JudgeHeatConfirm';
import McHeatConfirm from './pages/McHeatConfirm';
import StageHandHeatConfirm from './pages/StageHandHeatConfirm';

// Judge
import JudgeDashboard from './pages/judge/JudgeDashboard';
import JudgeEvaluate from './pages/judge/JudgeEvaluate';
import JudgeSetup from './pages/judge/JudgeSetup';

// Viewer (role 'observer') — the Results tab on its own
import ViewerResults from './pages/viewer/ViewerResults';

// Admin
import AdminDashboard       from './pages/admin/AdminDashboard';
import AdminResults         from './pages/admin/AdminResults';
import PlacementCategories  from './pages/admin/PlacementCategories';
import AdminSongReview      from './pages/admin/AdminSongReview';

export default function App() {
  return (
    <AuthProvider>
      <TooltipProvider>
      <BrowserRouter>
        <ArchiveBanner />
        <Routes>
          {/* Public routes */}
          <Route path="/login"                  element={<LoginPage />} />
          <Route path="/register"               element={<RegisterPage />} />
          <Route path="/judge-setup"            element={<JudgeSetup />} />
          <Route path="/reset-password"         element={<ResetPassword />} />
          <Route path="/heat-confirm/:token"    element={<HeatConfirm />} />
          <Route path="/judge-heat-confirm/:token" element={<JudgeHeatConfirm />} />
          <Route path="/mc-heat-confirm/:token" element={<McHeatConfirm />} />
          <Route path="/stage-hand-heat-confirm/:token" element={<StageHandHeatConfirm />} />

          {/* Contestant routes */}
          <Route
            path="/contestant/dashboard"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <ContestantDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contestant/submit"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <ContestantSubmit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contestant/profile"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <ContestantProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contestant/payment"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <ContestantPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contestant/songs/:songId"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <ContestantSongReview />
              </ProtectedRoute>
            }
          />

          {/* Heat selection */}
          <Route
            path="/heat-selection"
            element={
              <ProtectedRoute allowedRoles={['contestant', 'admin']}>
                <HeatSelection />
              </ProtectedRoute>
            }
          />

          {/* Judge routes */}
          <Route
            path="/judge/dashboard"
            element={
              <ProtectedRoute allowedRoles={['judge', 'admin']}>
                <JudgeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/judge/evaluate/:songId"
            element={
              <ProtectedRoute allowedRoles={['judge', 'admin']}>
                <JudgeEvaluate />
              </ProtectedRoute>
            }
          />

          {/* Viewer route — a viewer account lands here and sees only this.
              Admin passes too (hasRole treats admin as a superuser), so an
              admin can check what a viewer sees. */}
          <Route
            path="/viewer/results"
            element={
              <ProtectedRoute allowedRoles={['observer']}>
                <ViewerResults />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/results"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/placement-categories"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PlacementCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/songs/:songId/review"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSongReview />
              </ProtectedRoute>
            }
          />

          {/* Default: redirect / to /login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 catch-all. This used to redirect to /login, which made a dead
              link indistinguishable from "please sign in" - see NotFoundPage. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  );
}
